import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { FiCalendar, FiClock, FiMapPin, FiUsers } from 'react-icons/fi'
import { getEvent, getEventPerformances } from '../services/api'
import useAuthStore from '../stores/authStore'

import { getCategoryColor, getCategoryLabel, getHeroImageByCategory } from '../helpers/category'


export default function EventPage() {
    const { slug } = useParams()
    const { isAuthenticated } = useAuthStore()
    const [event, setEvent] = useState(null)
    const [performances, setPerformances] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedDate, setSelectedDate] = useState(null)

    const currentLanguage = localStorage.getItem('app_lang')

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [])

    useEffect(() => {
        loadEvent()
    }, [slug])

    const loadEvent = async () => {
        try {
            const eventRes = await getEvent(slug)
            setEvent(eventRes.data.event)

            const perfRes = await getEventPerformances(slug)
            setPerformances(perfRes.data.data || perfRes.data)
        } catch (error) {
            console.error('Failed to load event', error)
        } finally {
            setLoading(false)
        }
    }

    // Group performances by date
    const performancesByDate = performances.reduce((acc, perf) => {
        const date = new Date(perf.start_time).toDateString()
        if (!acc[date]) acc[date] = []
        acc[date].push(perf)
        return acc
    }, {})

    const dates = Object.keys(performancesByDate).sort((a, b) => new Date(a) - new Date(b))

    const uniqueHallsWithImages = Object.values(
        performances.reduce((acc, p) => {
            if (p.hall && p.hall.images && p.hall.images.length > 0) {
                acc[p.hall.id] = p.hall
            }
            return acc
        }, {})
    )

    const getImgUrl = (path) => {
        if (!path) return ''
        if (path.startsWith('http')) return path
        const base = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'
        return `${base}${path}`
    }

    if (loading) {
        return (
            <div className="min-h-screen pt-16 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        )
    }

    if (!event) {
        return (
            <div className="min-h-screen pt-16 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-white mb-4">{window.t('sabitieto_ne_e_namereno')}</h1>
                    <Link to="/" className="btn-primary">{window.t('obratno_kam_sabitiyata')}</Link>
                </div>
            </div>
        )
    }


    return (
        <div className="min-h-screen pt-16">
            {/* Hero */}
            <div className="relative h-[400px]">
                {/* 
                {event.image_url ? (
                    <img
                        src={event.image_url.startsWith('http') ? event.image_url : `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}${event.image_url}`}
                        alt={event.title}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary-600 to-accent-600" />
                )} 
                */}

                <img
                    src={`/hero-events/${getHeroImageByCategory(event.category)}`}
                    className="w-full h-full object-cover opacity-20"
                />
                {/* <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/50 to-transparent" /> */}
            </div>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-64 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 mb-8">
                        <div className="card p-8">
                            {/* Category Badge */}
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500/20 mb-4 text-gray-900 bg-gradient-to-r ${getCategoryColor(event.category)}`}>
                                {getCategoryLabel(event.category)?.toUpperCase()}
                            </span>

                            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                {currentLanguage === 'en' ? (event.title_en || event.title) : event.title}
                            </h1>

                            <div className="flex flex-wrap gap-4 text-gray-400 mb-6">
                                {event.duration_minutes && (
                                    <div className="flex items-center gap-2">
                                        <FiClock className="w-5 h-5" />
                                        {event.duration_minutes} {window.t('min')}
                                    </div>
                                )}
                                {event.age_restriction && (
                                    <div className="flex items-center gap-2">
                                        <FiUsers className="w-5 h-5" />
                                        {event.age_restriction}
                                    </div>
                                )}
                            </div>
                        </div>

                        {event.image_url && (
                            <div className="mt-8">
                                <img
                                    src={event.image_url.startsWith('http') ? event.image_url : `${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'}${event.image_url}`}
                                    alt={event.title}
                                    className="max-w-[80%] m-auto rounded-[10px]"
                                />
                            </div>
                        )}

                        <div className="card p-8 mt-8">
                            <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                                {currentLanguage === 'en' ? (event.description_en || event.description) : event.description}
                            </p>
                        </div>

                        {/* Performances */}
                        <div className="card p-8 mt-8">
                            <h2 className="text-xl font-bold text-white mb-6">{window.t('izberete_prozhektsiya_predstav')}</h2>

                            {/* Date Tabs */}
                            <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
                                {dates.map(date => {
                                    const d = new Date(date)
                                    const isSelected = selectedDate === date

                                    return (
                                        <button
                                            key={date}
                                            onClick={() => setSelectedDate(date)}
                                            className={`flex flex-col items-center px-4 py-3 rounded-xl transition-all ${isSelected
                                                ? 'bg-primary-500 text-white'
                                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                                }`}
                                        >
                                            <span className="text-xs font-medium uppercase truncate block w-full text-center">
                                                {d.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'bg-BG', { weekday: 'short' })}
                                            </span>
                                            <span className="text-lg font-bold">
                                                {d.getDate()}
                                            </span>
                                            <span className="text-xs uppercase truncate block w-full text-center">
                                                {d.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'bg-BG', { month: 'short' })}
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>

                            {/* Showtimes */}
                            {selectedDate && performancesByDate[selectedDate] && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                    {performancesByDate[selectedDate].map(performance => {
                                        const time = new Date(performance.start_time).toLocaleTimeString('bg-BG', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: false
                                        })

                                        const isDisabled = parseFloat(performance.base_price) === 0

                                        return (

                                            isDisabled ? (
                                                <div
                                                    key={performance.id}
                                                    className="card p-4 text-center opacity-80"
                                                >
                                                    <div className="text-lg font-bold text-white">
                                                        {time}
                                                    </div>

                                                    <div className="text-sm text-gray-500 mt-1">
                                                        {currentLanguage === 'en'
                                                            ? (performance.hall?.name_en || performance.hall?.name)
                                                            : performance.hall?.name}
                                                    </div>

                                                    <div className="text-primary-400 font-bold mt-2">
                                                        €{parseFloat(performance.base_price)}
                                                    </div>
                                                </div>
                                            ) : (
                                                <Link
                                                    key={performance.id}
                                                    to={
                                                        isAuthenticated
                                                            ? `/checkout/${performance.id}`
                                                            : `/login?redirect=/checkout/${performance.id}`
                                                    }
                                                    className="card p-4 text-center hover:border-primary-500 transition-colors group"
                                                >
                                                    <div className="text-lg font-bold text-white group-hover:text-primary-400 transition-colors">
                                                        {time}
                                                    </div>
                                                    <div className="text-sm text-gray-500 mt-1">
                                                        {currentLanguage === 'en'
                                                            ? (performance.hall?.name_en || performance.hall?.name)
                                                            : performance.hall?.name
                                                        }
                                                    </div>
                                                    <div className="text-primary-400 font-bold mt-2">
                                                        €{parseFloat(performance.base_price)}
                                                    </div>
                                                </Link>
                                            )

                                        )
                                    })}
                                </div>
                            )}

                            {!selectedDate && (
                                <p className="text-gray-500 text-center py-8 border border-gray-800 rounded-[5px]">
                                    {window.t('izberete_data_po_gore_za_da_vi')}
                                </p>
                            )}
                        </div>

                        {/* Venue Galleries */}
                        {uniqueHallsWithImages.length > 0 && (
                            <div className="card p-8 mt-8">
                                <h2 className="text-xl font-bold text-white mb-6">{window.t('razgledayte_zalite')}</h2>
                                <div className="space-y-8">
                                    {uniqueHallsWithImages.map(hall => (
                                        <div key={hall.id}>
                                            <h3 className="text-lg font-bold text-primary-400 mb-3">{currentLanguage === 'en' ? (hall.name_en || hall.name) : hall.name}</h3>
                                            <p className="text-gray-400 text-sm mb-4 whitespace-pre-line">
                                                {currentLanguage === 'en' ? (hall.description_en || hall.description) : hall.description}
                                            </p>
                                            <div className="flex gap-4 overflow-x-auto pb-4 snap-x">
                                                {hall.images.map((img, idx) => (
                                                    <div key={idx} className="shrink-0 w-64 h-40 snap-start">
                                                        <img src={getImgUrl(img)} alt={`${hall.name} view ${idx + 1}`} className="w-full h-full object-cover rounded-xl border border-gray-800" />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="card p-6 sticky top-24">
                            <h3 className="text-lg font-bold text-white mb-4">{window.t('barza_informatsiya')}</h3>

                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <FiCalendar className="w-5 h-5 text-primary-400 mt-0.5" />
                                    <div>
                                        <div className="text-white font-medium">{window.t('nalichni_dati')}</div>
                                        <div className="text-gray-400 text-sm">
                                            {dates.length} {window.t('predstoyashti_sabitiya', 'upcoming events').toLowerCase()}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <FiMapPin className="w-5 h-5 text-primary-400 mt-0.5" />
                                    <div>
                                        <div className="text-white font-medium">{window.t('zali')}</div>
                                        <div className="text-gray-400 text-sm">
                                            {[...new Set(performances.map(p => currentLanguage === 'en' ? (p.hall?.name_en || p.hall?.name) : p.hall?.name))].join(', ')}
                                        </div>
                                    </div>
                                </div>

                                {performances.length > 0 && (
                                    <div className="pt-4 border-t border-gray-800">
                                        <div className="text-gray-400 text-sm">{window.t('tseni_ot')}</div>
                                        <div className="text-3xl font-bold text-white">
                                            €{Math.min(...performances.map(p => parseFloat(p.base_price)))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
